import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './AgentCorePage.css';

// Webmail.API (port 5002), versioned route prefix. The AgentCore admin controller lives
// at /api/v1/agentcore on Webmail.API — NOT CRM.API (:5000).
const API_BASE = process.env.REACT_APP_WEBMAIL_API_URL || 'http://localhost:5002';

function AgentCorePage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('status');
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState([]);
  const [models, setModels] = useState([]);
  const [lookupTables, setLookupTables] = useState([]);
  const [selectedLookup, setSelectedLookup] = useState(null);
  const [lookupItems, setLookupItems] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [providerFactories, setProviderFactories] = useState([]);
  // Local edits keyed by `${factoryId}:${providerId}:${key}` so unsaved input survives re-render.
  const [providerEdits, setProviderEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  // Load initial data
  useEffect(() => {
    loadStatus();
    loadLookupTables();
  }, []);

  useEffect(() => {
    if (activeTab === 'settings') loadSettings();
    if (activeTab === 'models') loadModels();
    if (activeTab === 'escalation') loadEscalationRules();
    if (activeTab === 'providers') loadProviders();
  }, [activeTab]);

  const loadStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/status`, { headers });
      if (response.ok) {
        setStatus(await response.json());
      }
    } catch (err) {
      console.error('Failed to load status:', err);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/settings`, { headers });
      if (response.ok) {
        setSettings(await response.json());
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/models`, { headers });
      if (response.ok) {
        setModels(await response.json());
      }
    } catch (err) {
      setError('Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  const loadLookupTables = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/lookups`, { headers });
      if (response.ok) {
        setLookupTables(await response.json());
      }
    } catch (err) {
      console.error('Failed to load lookup tables:', err);
    }
  };

  const loadLookupItems = async (tableName) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/lookups/${tableName}`, { headers });
      if (response.ok) {
        setLookupItems(await response.json());
        setSelectedLookup(tableName);
      }
    } catch (err) {
      setError('Failed to load lookup items');
    } finally {
      setLoading(false);
    }
  };

  const loadEscalationRules = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/escalation-rules`, { headers });
      if (response.ok) {
        setEscalationRules(await response.json());
      }
    } catch (err) {
      setError('Failed to load escalation rules');
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/providers`, { headers });
      if (response.ok) {
        setProviderFactories(await response.json());
        setProviderEdits({});
      } else {
        setError('Failed to load providers');
      }
    } catch (err) {
      setError('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const setActiveProvider = async (factoryId, providerId) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/providers/${factoryId}/active`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ providerId })
      });
      if (response.ok) {
        setSuccess('Active provider updated');
        loadProviders();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to set active provider');
      }
    } catch (err) {
      setError('Failed to set active provider');
    }
  };

  const onProviderFieldChange = (factoryId, providerId, key, value) => {
    setProviderEdits(prev => ({ ...prev, [`${factoryId}:${providerId}:${key}`]: value }));
  };

  const saveProviderConfig = async (factory, provider) => {
    const fields = provider.fields.map(f => {
      const editKey = `${factory.factoryId}:${provider.providerId}:${f.key}`;
      const edited = providerEdits[editKey];
      return {
        key: f.key,
        // For secrets, only send the edited value (blank = "keep existing" on the server).
        value: edited !== undefined ? edited : (f.isSecret ? '' : f.value),
        isSecret: f.isSecret
      };
    });
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/agentcore/providers/${factory.factoryId}/${provider.providerId}/config`,
        { method: 'PUT', headers, body: JSON.stringify({ fields }) }
      );
      if (response.ok) {
        setSuccess(`Saved ${provider.providerName} configuration`);
        loadProviders();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save provider configuration');
      }
    } catch (err) {
      setError('Failed to save provider configuration');
    }
  };

  const updateSetting = async (settingId, value) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/settings/${settingId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ value })
      });
      if (response.ok) {
        setSuccess('Setting updated');
        loadSettings();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to update setting');
    }
  };

  const testOllama = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/agentcore/test-ollama`, { headers });
      const result = await response.json();
      if (result.available) {
        setSuccess(`Ollama connected: ${result.message}`);
      } else {
        setError(`Ollama test failed: ${result.message}`);
      }
    } catch (err) {
      setError('Failed to test Ollama');
    } finally {
      setLoading(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    }
  };

  const setDefaultModel = async (modelId, modelType) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/agentcore/models/${modelId}/set-default?modelType=${modelType}`,
        { method: 'POST', headers }
      );
      if (response.ok) {
        setSuccess(`Default ${modelType} model updated`);
        loadModels();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to set default model');
    }
  };

  // Group settings by category
  const settingsByCategory = settings.reduce((acc, s) => {
    const cat = s.categoryName || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <div className="agentcore-page">
      <h1>🤖 AgentCore Administration</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Tab Navigation */}
      <div className="tabs">
        <button className={activeTab === 'status' ? 'active' : ''} onClick={() => setActiveTab('status')}>
          System Status
        </button>
        <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
        <button className={activeTab === 'models' ? 'active' : ''} onClick={() => setActiveTab('models')}>
          LLM Models
        </button>
        <button className={activeTab === 'providers' ? 'active' : ''} onClick={() => setActiveTab('providers')}>
          Providers
        </button>
        <button className={activeTab === 'lookups' ? 'active' : ''} onClick={() => setActiveTab('lookups')}>
          Lookup Tables
        </button>
        <button className={activeTab === 'escalation' ? 'active' : ''} onClick={() => setActiveTab('escalation')}>
          Escalation Rules
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Status Tab */}
        {activeTab === 'status' && status && (
          <div className="status-panel">
            <h2>System Status</h2>
            <div className="status-grid">
              <div className={`status-card ${status.databaseConnected ? 'connected' : 'disconnected'}`}>
                <div className="status-icon">{status.databaseConnected ? '✅' : '❌'}</div>
                <div className="status-label">Database</div>
                <div className="status-value">{status.databaseConnected ? 'Connected' : 'Disconnected'}</div>
              </div>
              <div className={`status-card ${status.ollamaAvailable ? 'connected' : 'disconnected'}`}>
                <div className="status-icon">{status.ollamaAvailable ? '✅' : '⚠️'}</div>
                <div className="status-label">Ollama</div>
                <div className="status-value">{status.ollamaAvailable ? 'Available' : 'Not Available'}</div>
                <div className="status-detail">{status.ollamaUrl}</div>
              </div>
              <div className="status-card">
                <div className="status-icon">🔤</div>
                <div className="status-label">Embedding Model</div>
                <div className="status-value">{status.embeddingModel}</div>
              </div>
              <div className="status-card">
                <div className="status-icon">🧠</div>
                <div className="status-label">Chunking Model</div>
                <div className="status-value">{status.chunkingModel}</div>
              </div>
            </div>
            <div className="actions">
              <button onClick={testOllama} disabled={loading}>
                {loading ? 'Testing...' : 'Test Ollama Connection'}
              </button>
              <button onClick={loadStatus}>Refresh Status</button>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-panel">
            <h2>Agent Settings</h2>
            {loading ? <p>Loading...</p> : (
              Object.entries(settingsByCategory).map(([category, catSettings]) => (
                <div key={category} className="settings-category">
                  <h3>{category}</h3>
                  <table className="settings-table">
                    <thead>
                      <tr>
                        <th>Setting</th>
                        <th>Value</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catSettings.map(s => (
                        <tr key={s.settingId} className={s.isAdvanced ? 'advanced' : ''}>
                          <td>
                            <strong>{s.settingName}</strong>
                            <br />
                            <code>{s.settingCode}</code>
                          </td>
                          <td>
                            <input
                              type={s.valueTypeName === 'Integer' ? 'number' : 'text'}
                              defaultValue={s.settingValue}
                              onBlur={(e) => {
                                if (e.target.value !== s.settingValue) {
                                  updateSetting(s.settingId, e.target.value);
                                }
                              }}
                            />
                            {s.minValue && s.maxValue && (
                              <small>Range: {s.minValue} - {s.maxValue}</small>
                            )}
                          </td>
                          <td>{s.description}</td>
                          <td>
                            {!s.isActive && <span className="badge inactive">Inactive</span>}
                            {s.isAdvanced && <span className="badge advanced">Advanced</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </div>
        )}

        {/* Models Tab */}
        {activeTab === 'models' && (
          <div className="models-panel">
            <h2>LLM Models Configuration</h2>
            {loading ? <p>Loading...</p> : (
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Provider</th>
                    <th>Type</th>
                    <th>Dimensions</th>
                    <th>Context</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map(m => (
                    <tr key={m.modelId} className={m.isDefault ? 'default' : ''}>
                      <td>
                        <strong>{m.modelName}</strong>
                        <br />
                        <code>{m.modelCode}</code>
                      </td>
                      <td>{m.provider}</td>
                      <td>{m.modelType}</td>
                      <td>{m.embeddingDimensions || '-'}</td>
                      <td>{m.contextWindowTokens?.toLocaleString() || '-'}</td>
                      <td>
                        {m.isDefault && <span className="badge default">Default</span>}
                        {m.isActive ? <span className="badge active">Active</span> : <span className="badge inactive">Inactive</span>}
                      </td>
                      <td>
                        {!m.isDefault && m.isActive && (
                          <button onClick={() => setDefaultModel(m.modelId, m.modelType)}>
                            Set Default
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Providers Tab */}
        {activeTab === 'providers' && (
          <div className="providers-panel">
            <h2>LLM Provider Provisioning</h2>
            <p className="panel-hint">
              Each factory slot resolves one active provider per tenant. Configuration is stored
              in the standardised <code>ProviderConfig</code> store — the same one the runtime resolves.
              Secret values are masked and never returned to the browser.
            </p>
            {loading ? <p>Loading...</p> : (
              providerFactories.map(factory => (
                <div key={factory.factoryId} className="provider-factory">
                  <div className="provider-factory-header">
                    <h3>{factory.factoryName}</h3>
                    {factory.description && <small>{factory.description}</small>}
                  </div>
                  <div className="provider-cards">
                    {factory.providers.map(provider => {
                      const isActive = provider.isActive;
                      return (
                        <div
                          key={provider.providerId}
                          className={`provider-card ${isActive ? 'active' : ''} ${provider.isSelectable ? '' : 'unavailable'}`}
                        >
                          <div className="provider-card-header">
                            <div className="provider-title">
                              <strong>{provider.providerName}</strong>
                              {isActive && <span className="badge active">Active</span>}
                              {provider.isMvpDefault && <span className="badge default">Default</span>}
                              {!provider.isSelectable && <span className="badge inactive">Not implemented</span>}
                            </div>
                            {!isActive && provider.isSelectable && (
                              <button onClick={() => setActiveProvider(factory.factoryId, provider.providerId)}>
                                Make Active
                              </button>
                            )}
                          </div>
                          <div className="provider-fields">
                            {provider.fields.map(field => {
                              const editKey = `${factory.factoryId}:${provider.providerId}:${field.key}`;
                              const editVal = providerEdits[editKey];
                              const value = editVal !== undefined ? editVal : field.value;
                              return (
                                <div key={field.key} className="provider-field">
                                  <label>
                                    {field.label}
                                    {field.required && <span className="req">*</span>}
                                    {field.isSecret && field.hasValue && (
                                      <span className="secret-set">secret set</span>
                                    )}
                                  </label>
                                  <input
                                    type={field.type === 'password' ? 'password' : 'text'}
                                    value={value}
                                    placeholder={field.placeholder || (field.isSecret ? '••••••••' : '')}
                                    onChange={(e) => onProviderFieldChange(factory.factoryId, provider.providerId, field.key, e.target.value)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          {provider.isSelectable && (
                            <div className="provider-card-actions">
                              <button onClick={() => saveProviderConfig(factory, provider)}>
                                Save Configuration
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Lookup Tables Tab */}
        {activeTab === 'lookups' && (
          <div className="lookups-panel">
            <h2>Lookup Tables</h2>
            <div className="lookups-container">
              <div className="lookup-list">
                <h3>Tables</h3>
                <ul>
                  {lookupTables.map(t => (
                    <li 
                      key={t} 
                      className={selectedLookup === t ? 'selected' : ''}
                      onClick={() => loadLookupItems(t)}
                    >
                      {t.replace('lkp', '')}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lookup-items">
                {selectedLookup && (
                  <>
                    <h3>{selectedLookup.replace('lkp', '')} Items</h3>
                    {loading ? <p>Loading...</p> : (
                      <table>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lookupItems.map(item => (
                            <tr key={item.id}>
                              <td>{item.id}</td>
                              <td>{item.name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Escalation Rules Tab */}
        {activeTab === 'escalation' && (
          <div className="escalation-panel">
            <h2>Escalation Rules</h2>
            {loading ? <p>Loading...</p> : (
              <table className="escalation-table">
                <thead>
                  <tr>
                    <th>Rule</th>
                    <th>Module</th>
                    <th>Channel</th>
                    <th>Condition</th>
                    <th>Action</th>
                    <th>Priority</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {escalationRules.map(r => (
                    <tr key={r.ruleId} className={r.isActive ? '' : 'inactive'}>
                      <td>
                        <strong>{r.ruleName}</strong>
                        <br />
                        <small>{r.description}</small>
                      </td>
                      <td>{r.sourceModuleName || 'All'}</td>
                      <td>{r.channelName || 'All'}</td>
                      <td>
                        <code>{r.conditionLogic}</code>
                      </td>
                      <td>{r.actionName}</td>
                      <td>{r.priority}</td>
                      <td>
                        {r.isActive ? 
                          <span className="badge active">Active</span> : 
                          <span className="badge inactive">Inactive</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentCorePage;



